import React, { useState, useCallback, useEffect, useRef } from "react";

export type MakulaImageFit = "cover" | "contain" | "fill" | "none";

export interface MakulaImageProps {
  /** Image source URL */
  src?: string | null;
  /** Alt text for the image */
  alt?: string;
  /** Fallback content when image fails or is not provided */
  fallback?: React.ReactNode;
  /** Object fit style */
  fit?: MakulaImageFit;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the image element */
  imageClassName?: string;
  /** Border radius class or style */
  rounded?: string;
  /** Callback when image loads successfully */
  onLoad?: () => void;
  /** Callback when image fails to load */
  onError?: () => void;
}

// Makula logo icon (hexagon shape)
const MakulaLogoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 26 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="m16.287.904 6.137 3.536a6.784 6.784 0 0 1 3.395 5.878v11.364a6.784 6.784 0 0 1-3.395 5.878l-6.137 3.536a6.764 6.764 0 0 1-6.755 0L3.395 27.56A6.784 6.784 0 0 1 0 21.682V10.318A6.784 6.784 0 0 1 3.395 4.44L9.532.904a6.764 6.764 0 0 1 6.755 0ZM9.655 7.075l-.064.037-5.355 3.096a6.648 6.648 0 0 0-3.318 5.684v.145c0 2.35 1.24 4.524 3.257 5.72l.061.035 5.355 3.096a6.628 6.628 0 0 0 6.573.037l.064-.037 5.355-3.096a6.648 6.648 0 0 0 3.318-5.684v-.145c0-2.35-1.24-4.524-3.257-5.72l-.061-.035-5.355-3.096a6.629 6.629 0 0 0-6.573-.037Z"
      fill="#0517F8"
      fillOpacity="0.3"
    />
  </svg>
);

/**
 * MakulaImage
 *
 * A base image component with built-in loading animation.
 * Shows pulse animation with Makula logo while loading, smooth fade-in on load.
 *
 * Features:
 * - Pulse loading animation with Makula branding
 * - Shimmer effect overlay
 * - Smooth fade-in transition
 * - Error handling with fallback
 * - Configurable object-fit
 * - Caches loaded state to prevent re-fetching
 */
export const MakulaImage: React.FC<MakulaImageProps> = ({
  src,
  alt = "",
  fallback,
  fit = "cover",
  className = "",
  imageClassName = "",
  rounded = "rounded-lg",
  onLoad,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(!!src);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset loading state when src changes
  useEffect(() => {
    if (src) {
      setIsLoading(true);
      setHasError(false);

      // Check if image is already cached/complete
      // This handles the case when component remounts with a cached image
      const img = imgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  }, [onError]);

  // No src or error - show fallback
  if (!src || hasError) {
    return (
      <div
        className={`relative w-full h-full overflow-hidden ${rounded} ${className}`}
      >
        {fallback || (
          <div className="w-full h-full bg-brand-lightest flex items-center justify-center">
            <MakulaLogoIcon className="w-1/3 h-1/3 opacity-50" />
          </div>
        )}
      </div>
    );
  }

  const fitClasses: Record<MakulaImageFit, string> = {
    cover: "object-cover",
    contain: "object-contain",
    fill: "object-fill",
    none: "object-none",
  };

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${rounded} ${className}`}
    >
      {/* Inject keyframes at top level */}
      <style>
        {`
          @keyframes makulaShimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes makulaPulse {
            0%, 100% { opacity: 1; background-color: #F0F4FF; }
            50% { opacity: 0.6; background-color: #E8EEFF; }
          }
        `}
      </style>

      {/* Loading placeholder with pulse animation and Makula branding */}
      {isLoading && (
        <div
          className="absolute inset-0 flex items-center justify-center overflow-hidden"
          style={{
            backgroundColor: "#F0F4FF",
            animation: "makulaPulse 1.5s ease-in-out infinite",
          }}
        >
          {/* Makula logo */}
          <MakulaLogoIcon className="w-1/4 h-1/4 min-w-[24px] min-h-[24px]" />

          {/* Shimmer overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
              backgroundSize: "200% 100%",
              animation: "makulaShimmer 1.5s ease-in-out infinite",
            }}
          />
        </div>
      )}

      {/* Actual image */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`
          w-full h-full ${fitClasses[fit]} ${rounded}
          transition-opacity duration-300 ease-in-out
          ${isLoading ? "opacity-0" : "opacity-100"}
          ${imageClassName}
        `}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};
